import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommentStatus } from '@completion/enums';
import { StatusChangeNote } from '@completion/models';
import { Store } from '@ngrx/store';
import { getCurrentDocument } from '@completion/selectors';
import { take } from 'rxjs/operators';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { StatusPillComponent } from '../../../../shared/status-pill/status-pill.component';

interface DialogData {
  commentId: number;
  currentStatus: CommentStatus | string;
}

@Component({
  selector: 'app-status-change-dialog',
  templateUrl: './status-change-dialog.component.html',
  styleUrls: ['./status-change-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatListModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    MatDividerModule,
    StatusPillComponent,
  ],
})
export class StatusChangeDialogComponent implements OnInit {
  form: FormGroup;
  statuses = [
    CommentStatus.CREATED,
    CommentStatus.IN_PROGRESS,
    CommentStatus.FIXED,
    CommentStatus.DEFERRED,
    CommentStatus.NOT_APPLICABLE,
    CommentStatus.PUSHED_BACK,
  ];

  public notes: StatusChangeNote[] = [];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<StatusChangeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private store: Store,
  ) {
    this.form = this.fb.group({
      changeTo: [data.currentStatus || '', Validators.required],
      changeNote: ['', [Validators.maxLength(500)]],
    });
  }

  ngOnInit(): void {
    // Load existing status-change-notes for the comment if present in current document
    try {
      const cid = Number(this.data?.commentId);
      if (!isNaN(cid)) {
        this.store
          .select(getCurrentDocument)
          .pipe(take(1))
          .subscribe((doc) => {
            if (!doc || !doc.nodes) return;
            const comment = this.findCommentInNodes(doc.nodes, cid);
            if (comment && Array.isArray((comment as any).statusChangeNotes)) {
              const raw = (comment as any).statusChangeNotes as StatusChangeNote[];
              // Ensure ids and commentId are numeric and parse dates
              this.notes = raw.map(
                (n) =>
                  ({
                    ...n,
                    id: n.id != null ? Number(n.id) : Date.now() * -1,
                    commentId: Number(n.commentId),
                    changeDate: n.changeDate ? new Date(n.changeDate).toISOString() : new Date().toISOString(),
                  }) as StatusChangeNote,
              );
              // Sort descending by changeDate
              this.notes.sort((a, b) => new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime());
            }
          });
      }
    } catch {
      // ignore retrieval failures
    }
  }

  private findCommentInNodes(nodes: any[] | undefined, commentId: number): any | null {
    if (!Array.isArray(nodes)) return null;
    for (const n of nodes) {
      if (!n) continue;
      const comments = Array.isArray(n.comments) ? n.comments : [];
      for (const c of comments) {
        const cid = c.id ?? c.commentId;
        if (cid == null) continue;
        if (Number(cid) === commentId) return c;
      }
      if (Array.isArray(n.children) && n.children.length) {
        const found = this.findCommentInNodes(n.children, commentId);
        if (found) return found;
      }
    }
    return null;
  }

  // Human-friendly label for statuses (used by template)
  getStatusLabel(status: any): string {
    if (!status) return '';
    const s = String(status).toUpperCase();
    switch (s) {
      case CommentStatus.CREATED:
        return 'Created';
      case CommentStatus.IN_PROGRESS:
        return 'In progress';
      case CommentStatus.FIXED:
        return 'Fixed';
      case CommentStatus.DEFERRED:
        return 'Deferred';
      case CommentStatus.NOT_APPLICABLE:
        return 'N/A';
      case CommentStatus.PUSHED_BACK:
        return 'Pushed back';
      default:
        return s;
    }
  }

  confirm() {
    if (this.form.invalid) return;
    const payload: Partial<StatusChangeNote> = {
      commentId: this.data.commentId,
      changeFrom: this.data.currentStatus as any,
      changeTo: this.form.value.changeTo,
      changeNote: this.form.value.changeNote,
    };
    this.dialogRef.close(payload);
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
